/**
 * App-side wiring for `@heidi/templates/data` (S2, ARCFE-1014).
 *
 * What crosses the seam, and nothing else:
 *
 * - `globalHttp` — the app-built UNAUTHENTICATED client the public template
 *   gallery uses. It is deliberately not resolved from `getScribeHttpClients()`:
 *   the gallery is readable without a session, so it carries none of the scribe
 *   interceptors. Same reasoning as the EHR `inboxHttp` precedent — a
 *   domain-specific client is injected as its own instance.
 * - `queryClient` — the app's singleton, for the carved module-scope
 *   invalidators. The LEGACY shape, kept because those consumers exist and
 *   work.
 * - `getQueryClient` — the SAME singleton as a GETTER, which is the ruled shape
 *   for every package that needs the app cache (task 2.7 of
 *   `openspec/changes/monorepo-ui-endgame`). Both are wired from one instance,
 *   so the two can never disagree about which cache is addressed; resolving per
 *   call is what lets a module-scope consumer be evaluated before this wiring
 *   runs without racing client construction, which the field cannot offer. The
 *   field is not removed here — its consumers are carved and working, and
 *   converting them is its own change.
 * - `isDesktopOfflineNetworkError` — the desktop offline predicate (D2.7,
 *   ARCFE-1046). It reads `isTauri()` and the `offlineConnectivityStateAtom`
 *   via jotai's `getDefaultStore()`, so it is app+Tauri state by construction
 *   and cannot move into a headless package. The carved list reads branch on
 *   it to decide between a cached page, a swallow, and a rethrow.
 *
 * The scribe client family is NOT injected here: the 34 `@domain templates`
 * endpoints read the `@heidi/api/client` registry, which `scribeHTTP.ts`
 * populates at factory time (E5, ARCFE-805).
 *
 * Imported at module scope in `_app.tsx` alongside the other data wirings.
 */
import { getDomain } from '@/helpers/getDomains';
import { globalHttp } from '@/utils/globalHTTP';
import { isDesktopOfflineNetworkError } from '@/utils/http/desktopOfflineNetwork';
import { getAuthTokenForHttpClientSync } from '@/utils/http/getAuthTokenForHttpClientSync';
import { isOnPremRuntime } from '@/utils/http/onPremOptionalMetadataHeaders';
import { buildScribeSseHeaders } from '@/utils/http/scribeSseHeaders';
import { reactQueryClient } from '@/utils/reactQueryClient';
import { handleStreamOpen } from '@/utils/sseErrorHandler';
import { configureTemplateStreamTransport } from '@heidi/templates';
import { configureTemplatesData } from '@heidi/templates/data';

configureTemplatesData({
  communityHttp: globalHttp,
  queryClient: reactQueryClient,
  getQueryClient: () => reactQueryClient,
  isOfflineNetworkError: isDesktopOfflineNetworkError,
});

/**
 * The template-generation SSE transport.
 *
 * A SECOND seam rather than five more members on the one above, because the
 * subject is different: `configureTemplatesData` hands over clients and caches
 * for the `/data` zone, while these five are what a raw `fetch` needs to reach
 * ml-scribe from the `/ui` stream — a region-resolved origin, the synchronous
 * token read, the on-prem runtime predicate, the shared scribe SSE header
 * builder, and the shared non-OK `onopen` handler. Each reads app or platform
 * state (Redux, jotai, `isTauri()`, the build profile), so none can move.
 *
 * Bound here because this module is already imported at `_app.tsx` module
 * scope, which is earlier than any stream can start.
 */
configureTemplateStreamTransport({
  getDomain,
  getAuthTokenForHttpClientSync,
  isOnPremRuntime,
  buildScribeSseHeaders,
  handleStreamOpen,
});
